import React, { useState, useRef, useEffect, useCallback, forwardRef } from "react";
import ReactDOM from "react-dom";
import {FONT, RADIUS} from "../lib/theme.js";
import { useTheme } from "../lib/ThemeContext.jsx";
import FIELD_TIPS from "../lib/fieldTips.js";
import { SAVE_STATUS_COLORS, SAVE_STATUS_LABELS } from "../hooks/useData.js";

// ─── Global animation styles ──────────────────────────────────────────────────
const ANIM_STYLES = `
* { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
html, body { margin: 0; padding: 0; box-sizing: border-box; }
*, *::before, *::after { box-sizing: inherit; }
:focus-visible { outline: 2px solid #8b5cf6; outline-offset: 2px; border-radius: 4px; }
.sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
.skip-to-content { position:absolute; top:-40px; left:0; padding:8px 16px; background:#8b5cf6; color:#fff; z-index:99999; font-size:14px; font-weight:700; text-decoration:none; transition:top 0.2s; }
.skip-to-content:focus { top:0; }
.sf-anim-slide-in { animation: sfSlideIn 0.3s cubic-bezier(0.34,1.3,0.64,1) both; }
.sf-anim-modal    { animation: sfModalIn 0.28s cubic-bezier(0.34,1.3,0.64,1) both; }
.sf-tab-enter     { animation: sfTabEnter 0.28s cubic-bezier(0.34,1.3,0.64,1) both; }
.sf-spring-in     { animation: sfSpringIn 0.32s cubic-bezier(0.34,1.5,0.64,1) both; }
.sf-editable {
  background: rgba(120,120,128,0.1) !important;
  border: 1.5px solid transparent !important;
  border-radius: 10px !important;
  padding: 3px 8px !important;
  cursor: text;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s !important;
  outline: none !important;
}
.sf-editable:hover { background: rgba(120,120,128,0.16) !important; }
.sf-editable:focus {
  background: rgba(0,113,227,0.08) !important;
  border-color: rgba(139,92,246,0.5) !important;
  box-shadow: 0 0 0 3.5px rgba(0,113,227,0.15) !important;
  outline: none !important;
}
.sf-editable-num {
  background: rgba(120,120,128,0.1);
  border: 1.5px solid transparent;
  border-radius: 10px;
  padding: 3px 8px;
  cursor: text;
  transition: background 0.2s, border-color 0.2s, box-shadow 0.2s;
  outline: none;
  -moz-appearance: textfield;
}
.sf-editable-num:hover { background: rgba(120,120,128,0.16); }
.sf-editable-num:focus {
  background: rgba(0,113,227,0.08);
  border-color: rgba(139,92,246,0.5);
  box-shadow: 0 0 0 3.5px rgba(0,113,227,0.15);
  outline: none;
}
.sf-editable-num::-webkit-inner-spin-button,
.sf-editable-num::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
input[type=number]::-webkit-inner-spin-button,
input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
@keyframes sfTooltipIn  { from{opacity:0} to{opacity:1} }
@keyframes sfModalIn    { from{opacity:0;transform:scale(0.88) translateY(16px)} to{opacity:1;transform:scale(1) translateY(0)} }
@keyframes sfSlideIn    { from{opacity:0;transform:translateX(-20px) scale(0.96)} to{opacity:1;transform:translateX(0) scale(1)} }
@keyframes sfTabEnter   { from{opacity:0;transform:translateY(14px) scale(0.98)} to{opacity:1;transform:translateY(0) scale(1)} }
@keyframes sfSpringIn   { from{opacity:0;transform:scale(0.8)} to{opacity:1;transform:scale(1)} }
@keyframes sfFadeIn     { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
@keyframes sfParticle   { 0%{transform:translate(0,0) scale(1);opacity:1} 100%{transform:translate(var(--dx),var(--dy)) scale(0);opacity:0} }
@keyframes sfPop        { 0%{transform:scale(1)} 35%{transform:scale(1.14)} 65%{transform:scale(0.96)} 100%{transform:scale(1)} }
@keyframes sfWiggle     { 0%,100%{transform:rotate(0)} 20%{transform:rotate(-4deg)} 40%{transform:rotate(4deg)} 60%{transform:rotate(-2deg)} 80%{transform:rotate(2deg)} }
@keyframes sfRipple     { from{transform:scale(0);opacity:0.55} to{transform:scale(2.8);opacity:0} }
@keyframes sfPulseRed   { 0%,100%{box-shadow:0 0 0 0 rgba(255,59,48,0)} 50%{box-shadow:0 0 0 6px rgba(255,59,48,0.28)} }
@keyframes sfSpin       { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
@keyframes sfPulse      { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.6;transform:scale(0.94)} }
@keyframes sfDotPulse   { 0%,100%{transform:scale(1);box-shadow:0 0 0 0 currentColor} 50%{transform:scale(1.15);box-shadow:0 0 0 6px transparent} }
@keyframes sfPanelInRight { from{opacity:0;transform:translateX(-10px) scale(0.97)} to{opacity:1;transform:translateX(0) scale(1)} }
@keyframes sfPanelInLeft  { from{opacity:0;transform:translateX(10px) scale(0.97)} to{opacity:1;transform:translateX(0) scale(1)} }
@keyframes sfPanelInDown  { from{opacity:0;transform:translateY(-8px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
@keyframes sfSheetUp      { from{transform:translateY(100%)} to{transform:translateY(0)} }
@keyframes sfSheetIn    { from{opacity:0;transform:translate(-50%,-50%) scale(0.9)} to{opacity:1;transform:translate(-50%,-50%) scale(1)} }
@keyframes sfBounce     { 0%{transform:translateY(0)} 40%{transform:translateY(-6px)} 70%{transform:translateY(2px)} 100%{transform:translateY(0)} }

/* ── Global dark mode text visibility fix ── */
body { color: inherit; }
input, textarea, select, button, th, td, label, span, div, p, h1, h2, h3, h4 { color: inherit; }
table { color: inherit; }
input::placeholder { opacity: 0.45; }

/* ── Placeholder text visibility ── */
input::placeholder,textarea::placeholder{color:rgba(160,165,180,0.85)!important} .sf-light input::placeholder,.sf-light textarea::placeholder{color:rgba(20,20,30,0.6)!important;font-weight:500}
`;

let _stylesInjected = false;
function injectStyles() {
  if (_stylesInjected || typeof document === "undefined") return;
  const el = document.createElement("style");
  el.id = "sf-anim-styles";
  el.textContent = ANIM_STYLES;
  document.head.appendChild(el);
  _stylesInjected = true;
}

// ─── Tooltip (portal-based, no flicker) ───────────────────────────────────────
let _tooltipRoot = null;
function getTooltipRoot() {
  if (_tooltipRoot && document.body.contains(_tooltipRoot)) return _tooltipRoot;
  _tooltipRoot = document.createElement("div");
  _tooltipRoot.id = "sf-tooltip-root";
  _tooltipRoot.style.cssText = "position:fixed;top:0;left:0;width:0;height:0;z-index:99999;pointer-events:none;";
  document.body.appendChild(_tooltipRoot);
  return _tooltipRoot;
}

let _tipEl = null;
let _tipPinned = false;  // true when user clicked to pin the tooltip open

// Split a long string into bullets at sentence boundaries (preserves meaning,
// doesn't paraphrase). Returns array of sentence strings (no leading bullet).
function splitToBullets(s) {
  if (!s) return [];
  // Split on sentence terminators followed by space + capital, OR on explicit newlines.
  const parts = String(s)
    .replace(/\r\n/g, "\n")
    .split(/\n+|(?<=[.!?])\s+(?=[A-Z0-9•—–\-"'(])/)
    .map(p => p.trim())
    .filter(Boolean);
  return parts;
}

// Build the rich tooltip body from structured content (object) OR from a string.
// Structured shape: { label, desc, formula, affects, when, whenNot, example }
// String content with multiple sentences is auto-bulleted.
function buildTipBody(content) {
  const wrap = document.createElement("div");
  const isObj = content && typeof content === "object";

  function addSection(title, body, color = "#a78bfa") {
    if (!body) return;
    const lines = Array.isArray(body) ? body : splitToBullets(body);
    if (!lines.length) return;
    const sec = document.createElement("div");
    sec.style.cssText = "margin-bottom:8px;";
    if (title) {
      const h = document.createElement("div");
      h.textContent = title;
      h.style.cssText = `font-size:9px;font-weight:800;color:${color};text-transform:uppercase;letter-spacing:0.8px;margin-bottom:4px;`;
      sec.appendChild(h);
    }
    if (lines.length === 1) {
      const p = document.createElement("div");
      p.textContent = lines[0];
      p.style.cssText = "font-size:12px;line-height:1.5;color:#f0f2ff;";
      sec.appendChild(p);
    } else {
      const ul = document.createElement("ul");
      ul.style.cssText = "margin:0;padding:0 0 0 16px;list-style:disc;font-size:12px;line-height:1.5;color:#f0f2ff;";
      for (const line of lines) {
        const li = document.createElement("li");
        li.textContent = line;
        li.style.cssText = "margin-bottom:2px;";
        ul.appendChild(li);
      }
      sec.appendChild(ul);
    }
    wrap.appendChild(sec);
  }

  if (isObj) {
    if (content.label) {
      const lbl = document.createElement("div");
      lbl.textContent = content.label;
      lbl.style.cssText = "font-size:13px;font-weight:800;color:#a78bfa;margin-bottom:8px;";
      wrap.appendChild(lbl);
    }
    addSection("What", content.desc, "#a78bfa");
    addSection("How", content.formula, "#06b6d4");
    addSection("Affects", content.affects, "#34d399");
    addSection("Watch out", content.whenNot, "#f87171");
    if (content.example) addSection("Example", content.example, "#fbbf24");
    if (content.when && !content.whenNot) addSection("Use when", content.when, "#10b981");
  } else {
    addSection(null, String(content));
  }
  return wrap;
}

function showTip(content, targetEl, pin = false) {
  if (!content || !targetEl) return;
  // If already showing for this target and not pinning, do nothing
  if (_tipEl && !pin) return;
  hideTip();
  const r = targetEl.getBoundingClientRect();
  const root = getTooltipRoot();
  _tipEl = document.createElement("div");
  _tipPinned = pin;

  // Smart positioning: prefer below, flip above if near bottom edge
  const tipMaxH = 160;
  const spaceBelow = window.innerHeight - r.bottom;
  const above = spaceBelow < tipMaxH + 20 && r.top > tipMaxH + 20;

  // Horizontal: keep within viewport with 12px margin
  const approxW = 320;
  const rawLeft = r.left + r.width / 2;
  const clampedLeft = Math.min(Math.max(rawLeft, approxW / 2 + 12), window.innerWidth - approxW / 2 - 12);

  const topPos = above ? (r.top - 8) : (r.bottom + 8);

  _tipEl.style.cssText = `position:fixed;top:${topPos}px;left:${clampedLeft}px;transform:translateX(-50%)${above?" translateY(-100%)":""};background:#1a1e35;color:#f0f2ff;font-size:12px;line-height:1.5;padding:12px 16px;border-radius:10px;max-width:${approxW}px;font-family:${FONT};box-shadow:0 12px 40px rgba(0,0,0,0.7);animation:sfTooltipIn 0.12s ease both;white-space:normal;word-break:break-word;z-index:99999;pointer-events:${pin?"auto":"none"};border:1px solid rgba(167,139,250,0.28);`;
  _tipEl.appendChild(buildTipBody(content));

  // If pinned, add a close button and dismiss on outside click
  if (pin) {
    const closeBtn = document.createElement("span");
    closeBtn.textContent = "✕";
    closeBtn.style.cssText = "position:absolute;top:6px;right:8px;cursor:pointer;opacity:0.5;font-size:11px;";
    closeBtn.onclick = (e) => { e.stopPropagation(); hideTip(); };
    _tipEl.style.paddingRight = "24px";
    _tipEl.appendChild(closeBtn);

    // Dismiss on click outside
    setTimeout(() => {
      document.addEventListener("click", _outsideClickHandler, true);
    }, 0);
  }

  root.appendChild(_tipEl);
}

function _outsideClickHandler(e) {
  if (_tipEl && !_tipEl.contains(e.target)) {
    hideTip();
  }
}

function hideTip() {
  if (_tipPinned) {
    document.removeEventListener("click", _outsideClickHandler, true);
    _tipPinned = false;
  }
  if (_tipEl) { try { _tipEl.parentNode?.removeChild(_tipEl); } catch {} _tipEl = null; }
}

export function Tooltip({ content, children }) {
  injectStyles();
  // Hover-only by design — clicks pass through to the wrapped child so a
  // wrapped <button> still fires its own onClick. For click-to-pin rich
  // popups, use the <Info tip="..."/> component on a dedicated icon instead.
  return (
    <span
      onMouseEnter={e => { if (!_tipPinned) showTip(content, e.currentTarget, false); }}
      onMouseLeave={() => { if (!_tipPinned) hideTip(); }}
      style={{ display: "inline-flex", alignItems: "center" }}>
      {children}
    </span>
  );
}

// ─── Dropdown — fully custom select replacement (no native <select> popup) ───
// Use this where the OS-rendered option popup looks out of place. The trigger
// is a button, the popup is a portaled div with theme colors and rounded
// corners, and the API matches StyledSelect: pass `value`, `onChange`, and
// children that are <option value="...">label</option> just like a real select.
export function Dropdown({ value, onChange, children, style = {}, disabled, sm = false }) {
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const [popupStyle, setPopupStyle] = useState({});

  // Flatten <option>/<optgroup> children into a list of {value, label}
  const items = [];
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === "option") {
      items.push({ value: child.props.value, label: child.props.children });
    } else if (child.type === "optgroup") {
      React.Children.forEach(child.props.children, (sub) => {
        if (React.isValidElement(sub) && sub.type === "option") {
          items.push({ value: sub.props.value, label: sub.props.children });
        }
      });
    }
  });
  const current = items.find((it) => String(it.value) === String(value));
  const currentLabel = current ? current.label : "";

  const recompute = useCallback(() => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const margin = 8;
    const maxH = Math.min(320, vh - r.bottom - margin - 12);
    const above = maxH < 160 && r.top > 200;
    setPopupStyle({
      position: "fixed",
      left: Math.max(margin, Math.min(r.left, vw - r.width - margin)),
      top: above ? r.top - 4 : r.bottom + 4,
      width: r.width,
      transform: above ? "translateY(-100%)" : "none",
      maxHeight: above ? Math.min(320, r.top - margin - 8) : maxH,
      zIndex: 99998,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    recompute();
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    function onDocMouseDown(e) {
      if (triggerRef.current?.contains(e.target)) return;
      if (popupRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
      document.removeEventListener("mousedown", onDocMouseDown);
    };
  }, [open, recompute]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen(o => !o)}
        style={{
          width: "100%",
          textAlign: "left",
          appearance: "none",
          background: T.surface,
          border: `1px solid ${open ? T.accent : T.border}`,
          borderRadius: 12,
          color: T.text,
          padding: sm ? "6px 28px 6px 10px" : "9px 32px 9px 12px",
          fontSize: sm ? 12 : 13,
          outline: "none",
          fontFamily: FONT,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          transition: "border-color 0.18s ease",
          display: "block",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          ...style,
        }}
      >
        {currentLabel}
      </button>
      <span style={{
        position: "absolute",
        right: sm ? 10 : 12,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        color: open ? T.accent : T.textSub,
        fontSize: 9,
        transition: "color 0.18s ease",
      }}>▼</span>
      {open && ReactDOM.createPortal(
        <div ref={popupRef} style={{
          ...popupStyle,
          background: T.card || "#1a1e35",
          border: `1px solid ${T.accent}33`,
          borderRadius: 12,
          boxShadow: `0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px ${T.accent}11`,
          fontFamily: FONT,
          overflow: "auto",
          padding: 4,
          animation: "sfTooltipIn 0.12s ease both",
        }}>
          {items.map((it, idx) => {
            const selected = String(it.value) === String(value);
            return (
              <div
                key={idx}
                onClick={() => { onChange?.({ target: { value: it.value } }); setOpen(false); }}
                style={{
                  padding: sm ? "6px 10px" : "9px 12px",
                  fontSize: sm ? 12 : 13,
                  color: selected ? T.accent : T.text,
                  background: selected ? T.accent + "18" : "transparent",
                  borderRadius: 8,
                  cursor: "pointer",
                  fontWeight: selected ? 700 : 400,
                  transition: "background 0.12s ease",
                }}
                onMouseEnter={e => { if (!selected) e.currentTarget.style.background = T.surface; }}
                onMouseLeave={e => { if (!selected) e.currentTarget.style.background = "transparent"; }}
              >
                {it.label}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}

// ─── StyledSelect — app-standard styled <select> with custom chevron ─────────
// Use everywhere instead of a raw <select>. Drops the native browser arrow,
// uses theme colors, and supports a `sm` variant for inline rows.
export function StyledSelect({ value, onChange, children, style = {}, disabled, sm = false }) {
  const T = useTheme();
  // Recursively wrap <option> children so they inherit theme bg/color when
  // the OS dropdown popup opens (otherwise Windows / Linux render native white).
  const themedChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    if (child.type === "option" || child.type === "optgroup") {
      return React.cloneElement(child, {
        style: {
          background: T.card || "#1a1e35",
          color: T.text || "#f0f2ff",
          ...(child.props.style || {}),
        },
      });
    }
    return child;
  });
  return (
    <div style={{ position: "relative", width: "100%" }}>
      <select
        disabled={disabled}
        value={value}
        onChange={onChange}
        style={{
          width: "100%",
          appearance: "none",
          WebkitAppearance: "none",
          MozAppearance: "none",
          background: T.surface || "transparent",
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          color: T.text,
          padding: sm ? "6px 28px 6px 10px" : "9px 32px 9px 12px",
          fontSize: sm ? 12 : 13,
          outline: "none",
          fontFamily: FONT,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          transition: "border-color 0.18s ease",
          ...style,
        }}
      >{themedChildren}</select>
      <span style={{
        position: "absolute",
        right: sm ? 10 : 12,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        color: T.textSub,
        fontSize: 9,
      }}>▼</span>
    </div>
  );
}

// ─── Info Icon — supports simple string tips AND rich FIELD_TIPS lookup ───────
// Usage:
//   <Info tip="simple string"/>                           — plain hover tooltip
//   <Info tip="payout"/>                                  — FIELD_TIPS key, rich popup
//   <Info tip="batchArrival" tipVars={{date:"15.04.2026"}}/>  — interpolates {date} into desc/example
//
// Interpolation: any "{var}" placeholder in the tip's `desc`, `example`, `affects`,
// `formula`, `when`, `whenNot` is replaced with tipVars[var]. Missing vars stay as-is.

function interpolateTip(text, vars) {
  if (!text || !vars) return text;
  return String(text).replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
}

export function Info({ tip, saveStatus, tipVars }) {
  if (!tip) return null;
  const T = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Resolve: is this a FIELD_TIPS key or a plain string?
  const rawTip = FIELD_TIPS[tip];
  // Interpolate any {var} placeholders in the rich tip fields
  const richTip = rawTip ? {
    ...rawTip,
    desc:    interpolateTip(rawTip.desc, tipVars),
    affects: interpolateTip(rawTip.affects, tipVars),
    formula: interpolateTip(rawTip.formula, tipVars),
    example: interpolateTip(rawTip.example, tipVars),
    when:    interpolateTip(rawTip.when, tipVars),
    whenNot: interpolateTip(rawTip.whenNot, tipVars),
  } : null;

  // Close on outside click (only for rich popup)
  useEffect(() => {
    if (!open || !richTip) return;
    function handle(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open, richTip]);

  // Simple string — use existing Tooltip hover behavior
  if (!richTip) {
    return (
      <Tooltip content={tip}>
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 15, height: 15, borderRadius: "50%", border: `1.5px solid ${T.textSub||"#8b8fa8"}`,
          fontSize: 9, fontWeight: 700, color: T.textSub||"#8b8fa8",
          marginLeft: 4, cursor: "help", flexShrink: 0, userSelect: "none",
        }}>i</span>
      </Tooltip>
    );
  }

  // Rich FIELD_TIPS — click popup with full details
  const sc = SAVE_STATUS_COLORS[saveStatus] || SAVE_STATUS_COLORS.saved;
  const sl = SAVE_STATUS_LABELS[saveStatus] || "Saved";

  // Smart tooltip positioning — viewport-fixed, never clipped by parent overflow.
  // Computes both horizontal AND vertical position so the popup never goes
  // off-screen. Re-runs on window resize and scroll while open.
  // Two-phase render: first frame is invisible (visibility:hidden) at the
  // top-right with the correct width, second frame measures real height and
  // positions correctly with visibility:visible. This eliminates any flash.
  const [popupStyle, setPopupStyle] = useState({});
  const iconRef = useRef(null);
  const popupRef = useRef(null);

  const recomputePosition = useCallback(() => {
    if (!iconRef.current || !popupRef.current) return;
    const iconRect = iconRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 12;
    const popupW = Math.min(340, vw - margin * 2);
    // Measure the actual rendered popup height
    const popupH = Math.min(popupRef.current.offsetHeight, vh - margin * 2);

    // ── Horizontal: prefer right of icon, flip left if overflow ──
    let left = iconRect.right + 6;
    if (left + popupW > vw - margin) {
      // Try left of icon
      left = iconRect.left - popupW - 6;
    }
    // Final clamp — guarantees in-view
    left = Math.max(margin, Math.min(left, vw - popupW - margin));

    // ── Vertical: prefer below icon, flip above if overflow ──
    let top = iconRect.bottom + 8;
    if (top + popupH > vh - margin) {
      // Try above the icon
      const aboveTop = iconRect.top - popupH - 8;
      if (aboveTop >= margin) {
        top = aboveTop;
      } else {
        // Neither below nor above fits — clamp to viewport (max-height
        // on the popup container makes its inside scrollable)
        top = Math.max(margin, vh - popupH - margin);
      }
    }
    top = Math.max(margin, Math.min(top, vh - popupH - margin));

    setPopupStyle({
      position: "fixed",
      top, left,
      width: popupW,
      zIndex: 99999,
      visibility: "visible",
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    // Phase 1: hidden at top-left with the right width, so the popup can be
    // measured by the recompute call without any flicker.
    setPopupStyle({
      position: "fixed",
      top: 0,
      left: 0,
      width: Math.min(340, window.innerWidth - 24),
      zIndex: 99999,
      visibility: "hidden",
    });
    // Phase 2: measure + position on next frame
    const raf = requestAnimationFrame(() => recomputePosition());
    const onScroll = () => recomputePosition();
    const onResize = () => recomputePosition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open, recomputePosition]);

  // Outside-click handler — needs to ignore clicks INSIDE the portaled popup
  useEffect(() => {
    if (!open) return;
    function handle(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  return (
    <span ref={ref} style={{ position:"relative", display:"inline-flex", alignItems:"center" }}>
      <span ref={iconRef} onClick={e=>{e.preventDefault();e.stopPropagation();setOpen(!open);}}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 15, height: 15, borderRadius: "50%",
          border: `1.5px solid ${open ? T.accent : T.textSub||"#8b8fa8"}`,
          fontSize: 9, fontWeight: 700, color: open ? T.accent : T.textSub||"#8b8fa8",
          marginLeft: 4, cursor: "pointer", flexShrink: 0, userSelect: "none",
          background: open ? T.accent+"15" : "transparent", transition: "all 0.15s",
        }}>i</span>
      {saveStatus && saveStatus !== "saved" && (
        <span style={{ width:5, height:5, borderRadius:"50%", background:sc, marginLeft:3, flexShrink:0,
          animation: saveStatus==="saving"?"sfDotPulse 1.5s ease infinite":"none" }}/>
      )}
      {open && ReactDOM.createPortal(
        <div ref={popupRef} style={{
          ...popupStyle,
          background:T.card||"#1a1e35", border:`1px solid ${T.accent}33`,
          borderRadius: "12px", padding:"14px 16px", maxWidth:"calc(100vw - 24px)",
          boxShadow:`0 12px 40px rgba(0,0,0,0.5), 0 0 0 1px ${T.accent}11`,
          fontFamily: FONT, animation:"sfTooltipIn 0.12s ease both",
          maxHeight: "calc(100vh - 24px)", overflow: "auto",
        }}>
          {richTip.label && <div style={{ fontSize:13, fontWeight:800, color:T.accent, marginBottom:8 }}>{richTip.label}</div>}
          <div style={{ fontSize:12, color:T.text, lineHeight:1.6, marginBottom:10 }}>{richTip.desc}</div>
          {richTip.affects && (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:9, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.8, marginBottom:3 }}>Affects</div>
              <div style={{ fontSize:11, color:T.text, lineHeight:1.5 }}>{richTip.affects}</div>
            </div>
          )}
          {richTip.formula && (
            <div style={{ background:T.surface||T.bg, borderRadius:"6px", padding:"8px 10px", marginBottom:8, border:`1px solid ${T.border}` }}>
              <div style={{ fontSize:9, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.8, marginBottom:3 }}>Formula</div>
              <div style={{ fontSize:11, color:T.accent, fontWeight:600, lineHeight:1.5, fontFamily:"monospace" }}>{richTip.formula}</div>
            </div>
          )}
          {richTip.example && (
            <div style={{ marginBottom:8 }}>
              <div style={{ fontSize:9, fontWeight:700, color:T.textSub, textTransform:"uppercase", letterSpacing:0.8, marginBottom:3 }}>Example</div>
              <div style={{ fontSize:11, color:T.green||"#34c759", lineHeight:1.5 }}>{richTip.example}</div>
            </div>
          )}
          {(richTip.when || richTip.whenNot) && (
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {richTip.when && (
                <div>
                  <div style={{ fontSize:9, fontWeight:700, color:T.green||"#34c759", textTransform:"uppercase", letterSpacing:0.8, marginBottom:2 }}>✓ When to use</div>
                  <div style={{ fontSize:10, color:T.textSub, lineHeight:1.5 }}>{richTip.when}</div>
                </div>
              )}
              {richTip.whenNot && (
                <div>
                  <div style={{ fontSize:9, fontWeight:700, color:T.red||"#ff3b30", textTransform:"uppercase", letterSpacing:0.8, marginBottom:2 }}>✗ Avoid</div>
                  <div style={{ fontSize:10, color:T.textSub, lineHeight:1.5 }}>{richTip.whenNot}</div>
                </div>
              )}
            </div>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:10, paddingTop:8, borderTop:`1px solid ${T.border}` }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:sc }}/>
            <span style={{ fontSize:10, color:T.textSub }}>{sl}</span>
          </div>
        </div>,
        document.body
      )}
    </span>
  );
}

// ─── SaveDot — tiny save status indicator to place near any input ─────────────
// Usage: <SaveDot status={saveStatus} show={lastEditedId===myId}/>
export function SaveDot({ status="saved", show }) {
  if (!show) return null;
  const colors = { saved:"#34c759", saving:"#ff9f0a", error:"#ff3b30", offline:"#8e8e93" };
  const labels = { saved:"✓ Saved", saving:"Saving…", error:"✗ Failed", offline:"Offline" };
  const c = colors[status] || colors.saved;
  return (
    <span style={{ display:"inline-flex", alignItems:"center", gap:3, marginLeft:6, fontSize:9, color:c, fontWeight:600, fontFamily:FONT, flexShrink:0 }}>
      <span style={{ width:5, height:5, borderRadius:"50%", background:c,
        animation: status==="saving"?"sfDotPulse 1.5s ease infinite":"none" }}/>
      {labels[status]||""}
    </span>
  );
}

// ─── Apple Liquid Button (zero hooks — pure DOM events) ──────────────────────
export const Btn = forwardRef(function Btn({ children, onClick, color, variant = "solid", size = "md", disabled, style = {}, tip, danger }, ref) {
  injectStyles();
  const T = useTheme();
  const c = danger ? (T.red || "#ff3b30") : (color || T.blue || "#8b5cf6");

  // Vertical padding bumped from 4→7px on xs so touch targets clear the
  // iOS HIG / Android Material 40-44px minimum. Still visually compact.
  const sizes = {
    xs: { padding: "7px 10px", fontSize: 12, borderRadius: RADIUS.sm },
    sm: { padding: "8px 14px", fontSize: 13, borderRadius: RADIUS.sm },
    md: { padding: "10px 20px", fontSize: 15, borderRadius: RADIUS.md },
    lg: { padding: "14px 28px", fontSize: 17, borderRadius: RADIUS.pill },
  };
  const variants = {
    solid:   { background: c, color: (()=>{
      // Use dark text for yellow/amber/orange backgrounds
      const lc = (c||"").toLowerCase();
      const isLight = lc.includes('ffcc') || lc.includes('ffd6') || lc.includes('ff9500') ||
        lc.includes('f59e0b') || lc.includes('eab308') || lc.includes('fbbf24') || lc.includes('facc15');
      return isLight ? "#111" : "#fff";
    })(), border: "none" },
    outline: { background: "transparent", color: c, border: `1.5px solid ${c}` },
    ghost:   { background: "transparent", color: c, border: "none" },
    subtle:  { background: `${c+"18"}`, color: c, border: "none" },
  };

  const s = sizes[size] || sizes.md;
  const v = variants[variant] || variants.solid;

  function addRipple(e) {
    if (disabled) return;
    const btn = e.currentTarget;
    btn.style.transform = "scale(0.94)";
    const rect = btn.getBoundingClientRect();
    const ripple = document.createElement("span");
    ripple.style.cssText = `position:absolute;left:${e.clientX-rect.left-8}px;top:${e.clientY-rect.top-8}px;width:16px;height:16px;border-radius:50%;background:rgba(255,255,255,0.35);pointer-events:none;animation:sfRipple 0.6s ease-out both;`;
    btn.appendChild(ripple);
    setTimeout(() => { try { btn.removeChild(ripple); } catch {} }, 650);
  }
  function onUp(e) {
    e.currentTarget.style.transform = "scale(1.02)";
    setTimeout(() => { if (e.currentTarget) e.currentTarget.style.transform = "scale(1)"; }, 120);
  }
  function onEnter(e) {
    if (disabled) return;
    e.currentTarget.style.transform = "scale(1.03)";
    if (variant === "solid") e.currentTarget.style.boxShadow = `0 4px 16px ${c+"55"}`;
    if (danger) e.currentTarget.style.animation = "sfPulseRed 1s ease 2";
  }
  function onLeave(e) {
    e.currentTarget.style.transform = "scale(1)";
    e.currentTarget.style.boxShadow = variant === "solid" ? `0 2px 8px ${c+"30"}` : "none";
    e.currentTarget.style.animation = "none";
  }

  return (
    <Tooltip content={tip}>
      <button
        ref={ref}
        onClick={!disabled ? onClick : undefined}
        disabled={disabled}
        onMouseDown={addRipple}
        onMouseUp={onUp}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        style={{
          ...s, ...v,
          fontFamily: FONT, fontWeight: 600,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.45 : 1,
          display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
          position: "relative", overflow: "hidden",
          transition: "transform 0.12s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.15s",
          boxShadow: variant === "solid" ? `0 2px 8px ${c+"30"}` : "none",
          ...style,
        }}>
        {children}
      </button>
    </Tooltip>
  );
});

// ─── Editable text input (shows dashed underline as edit hint) ────────────────
export function EditableInp({ value, onChange, placeholder, style = {}, fontSize = 15, fontWeight = 400, color }) {
  const T = useTheme();
  return (
    <input value={value} onChange={onChange} placeholder={placeholder}
      className="sf-editable"
      style={{
        border: "none", padding: "2px 4px",
        color: color || T.text, fontSize, fontWeight,
        fontFamily: FONT, width: "100%",
        ...style,
      }}
    />
  );
}

// ─── Editable number input (zero hooks) ──────────────────────────────────────
export function EditableNum({ value, onChange, placeholder, style = {}, fontSize = 15, fontWeight = 700, color, min, max, step }) {
  const T = useTheme();
  return (
    <input type="number" value={value === 0 ? "" : (value || "")} placeholder={placeholder || "0"} min={min} max={max} step={step}
      className="sf-editable-num"
      onChange={e => { const p = parseFloat(e.target.value); onChange(isNaN(p) ? 0 : p); }}
      style={{
        border: "none", padding: "2px 4px",
        color: color || T.blue, fontSize, fontWeight,
        fontFamily: FONT, textAlign: "right",
        ...style,
      }}
    />
  );
}

// ─── Standard Input (zero hooks) ─────────────────────────────────────────────
export function Inp({ value, onChange, placeholder, type = "text", style = {}, disabled, onKeyDown, tip, min, max, step }) {
  const T = useTheme();
  const inp = (
    <input
      type={type} value={value} onChange={onChange} placeholder={placeholder}
      disabled={disabled} onKeyDown={onKeyDown} min={min} max={max} step={step}
      onFocus={e => {
        e.target.style.borderColor = T.blue || "#8b5cf6";
        e.target.style.boxShadow = `0 0 0 3px ${(T.blue || "#8b5cf6")+"22"}`;
      }}
      onBlur={e => {
        e.target.style.borderColor = T.border;
        e.target.style.boxShadow = "none";
      }}
      style={{
        background: T.surface || "#131629",
        border: `1.5px solid ${T.border || "rgba(255,255,255,0.08)"}`,
        borderRadius: RADIUS.md, color: T.text || "#f0f2ff",
        padding: "9px 14px", fontSize: 15, outline: "none", fontFamily: FONT,
        transition: "border-color 0.15s, box-shadow 0.15s",
        ...style,
      }}
    />
  );
  return <Tooltip content={tip}>{inp}</Tooltip>;
}

// ─── Date Picker ──────────────────────────────────────────────────────────────
export function DateInp({ value, onChange, placeholder = "Select date", style = {}, disabled }) {
  const T = useTheme();
  const [showPicker, setShowPicker] = useState(false);
  const [inputVal, setInputVal] = useState(value || "");
  const btnRef = useRef(null);      // ref on the 📅 button specifically
  const pickerRef = useRef(null);   // ref on the picker popup
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });

  useEffect(() => { setInputVal(value || ""); }, [value]);

  function parse(s) {
    if (!s) return null;
    const p = s.split(".");
    if (p.length !== 3) return null;
    const d = new Date(parseInt(p[2]), parseInt(p[1]) - 1, parseInt(p[0]));
    return isNaN(d.getTime()) ? null : d;
  }
  function fmtDate(d) {
    if (!d) return "";
    return `${String(d.getDate()).padStart(2,"0")}.${String(d.getMonth()+1).padStart(2,"0")}.${d.getFullYear()}`;
  }

  const parsed = parse(inputVal);
  const [calYear, setCalYear] = useState(() => parsed?.getFullYear() || new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(() => parsed?.getMonth() ?? new Date().getMonth());

  const PICKER_W = 252;
  const PICKER_H = 310;
  const GAP = 6;

  function computePos() {
    if (!btnRef.current) return { top: 100, left: 100 };
    const r = btnRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Prefer opening below; if not enough room open above
    let top, left;
    const spaceBelow = vh - r.bottom;
    const spaceAbove = r.top;

    if (spaceBelow >= PICKER_H + GAP) {
      top = r.bottom + GAP;
    } else if (spaceAbove >= PICKER_H + GAP) {
      top = r.top - PICKER_H - GAP;
    } else {
      // Not enough room either way — open below but clamp to viewport
      top = Math.max(GAP, Math.min(r.bottom + GAP, vh - PICKER_H - GAP));
    }

    // Align picker's right edge to button's right edge, then clamp to viewport
    left = r.right - PICKER_W;
    left = Math.max(GAP, Math.min(left, vw - PICKER_W - GAP));

    return { top, left };
  }

  function openPicker() {
    if (disabled) return;
    const p = parse(inputVal);
    setCalYear(p?.getFullYear() || new Date().getFullYear());
    setCalMonth(p?.getMonth() ?? new Date().getMonth());
    setPickerPos(computePos());
    setShowPicker(true);
  }

  // Recompute position on scroll/resize while open
  useEffect(() => {
    if (!showPicker) return;
    function update() { setPickerPos(computePos()); }
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => { window.removeEventListener("scroll", update, true); window.removeEventListener("resize", update); };
  }, [showPicker]);

  function selectDay(day) {
    const d = new Date(calYear, calMonth, day);
    const s = fmtDate(d);
    setInputVal(s); onChange(s); setShowPicker(false);
  }

  const MN = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const dim = new Date(calYear, calMonth + 1, 0).getDate();
  const firstDay = new Date(calYear, calMonth, 1).getDay();
  const offset = firstDay === 0 ? 6 : firstDay - 1;
  const today = new Date();
  const sel = parse(inputVal);

  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <input value={inputVal} onChange={e => { setInputVal(e.target.value); if (e.target.value.length === 10) onChange(e.target.value); else if (e.target.value === "") onChange(""); }}
        placeholder={placeholder} disabled={disabled}
        style={{
          background: T.surface || "#131629",
          border: `1.5px solid ${T.border || "rgba(255,255,255,0.08)"}`,
          borderRadius: `${RADIUS.md}px 0 0 ${RADIUS.md}px`,
          color: T.text || "#f0f2ff", padding: "9px 12px", fontSize: 14,
          outline: "none", fontFamily: FONT, width: 115,
          transition: "border-color 0.15s", ...style,
        }}
        onFocus={e => e.target.style.borderColor = T.blue || "#8b5cf6"}
        onBlur={e => e.target.style.borderColor = T.border || "rgba(255,255,255,0.08)"}
      />
      <button ref={btnRef} onClick={openPicker} disabled={disabled} style={{
        background: T.surface || "#131629",
        border: `1.5px solid ${T.border || "rgba(255,255,255,0.08)"}`, borderLeft: "none",
        borderRadius: `0 ${RADIUS.md}px ${RADIUS.md}px 0`,
        color: T.textSub || "#8b8fa8", padding: "9px 10px",
        cursor: disabled ? "not-allowed" : "pointer", fontSize: 14, lineHeight: 1,
      }}>📅</button>

      {showPicker && ReactDOM.createPortal(
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 10002 }} onClick={() => setShowPicker(false)}/>
          <div ref={pickerRef} style={{
            position: "fixed",
            top: pickerPos.top,
            left: pickerPos.left,
            zIndex: 10003,
            background: T.card || "#131629",
            border: `1px solid ${T.border || "rgba(255,255,255,0.12)"}`,
            borderRadius: RADIUS.lg, padding: 14, width: PICKER_W,
            boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
            animation: "sfFadeIn 0.12s ease both",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
              <button onClick={() => { if (calMonth===0){setCalMonth(11);setCalYear(y=>y-1);}else setCalMonth(m=>m-1); }}
                style={{ background:"none",border:"none",color:T.textSub,cursor:"pointer",fontSize:18,padding:"0 6px",fontFamily:FONT }}>‹</button>
              <span style={{ fontSize:14,fontWeight:700,color:T.text }}>{MN[calMonth]} {calYear}</span>
              <button onClick={() => { if (calMonth===11){setCalMonth(0);setCalYear(y=>y+1);}else setCalMonth(m=>m+1); }}
                style={{ background:"none",border:"none",color:T.textSub,cursor:"pointer",fontSize:18,padding:"0 6px",fontFamily:FONT }}>›</button>
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:4 }}>
              {["Mo","Tu","We","Th","Fr","Sa","Su"].map(d=>(
                <div key={d} style={{ textAlign:"center",fontSize:12,fontWeight:700,color:T.textMuted,padding:"4px 0" }}>{d}</div>
              ))}
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2 }}>
              {Array.from({length:offset},(_,i)=><div key={`e${i}`}/>)}
              {Array.from({length:dim},(_,i)=>{
                const day=i+1;
                const isSel=sel?.getDate()===day&&sel?.getMonth()===calMonth&&sel?.getFullYear()===calYear;
                const isToday=today.getDate()===day&&today.getMonth()===calMonth&&today.getFullYear()===calYear;
                return (
                  <button key={day} onClick={()=>selectDay(day)} style={{
                    background:isSel?T.blue||"#8b5cf6":"transparent",
                    color:isSel?"#fff":isToday?T.blue||"#8b5cf6":T.text,
                    border:isToday&&!isSel?`1px solid ${T.blue||"#8b5cf6"}`:"none",
                    borderRadius:RADIUS.sm,padding:"6px 2px",cursor:"pointer",
                    fontSize:13,fontFamily:FONT,fontWeight:isSel||isToday?700:400,
                  }}
                  onMouseEnter={e=>{if(!isSel)e.currentTarget.style.background=T.surfaceHi||"#1a1e35";}}
                  onMouseLeave={e=>{if(!isSel)e.currentTarget.style.background="transparent";}}>
                    {day}
                  </button>
                );
              })}
            </div>
            <div style={{ display:"flex",gap:6,marginTop:10,justifyContent:"flex-end" }}>
              <button onClick={()=>selectDay(today.getDate())} style={{ background:"none",border:"none",color:T.blue,cursor:"pointer",fontSize:11,fontFamily:FONT }}>Today</button>
              <button onClick={()=>{setInputVal("");onChange("");setShowPicker(false);}} style={{ background:"none",border:"none",color:T.textSub,cursor:"pointer",fontSize:11,fontFamily:FONT }}>Clear</button>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ─── Delay Transaction Picker ─────────────────────────────────────────────────
export function DelayPicker({ onDelay, onClose }) {
  const T = useTheme();
  const [mode, setMode] = useState("days");
  const [amount, setAmount] = useState(1);
  const [exactDate, setExactDate] = useState("");
  function handleConfirm() { onDelay({ mode, amount: parseInt(amount)||1, exactDate }); onClose(); }
  return (
    <div style={{ background:T.card,border:`1px solid ${T.border}`,borderRadius:RADIUS.lg,padding:20,width:280,boxShadow:"0 16px 48px rgba(0,0,0,0.5)",animation:"sfFadeIn 0.15s ease both" }}>
      <div style={{ fontSize:15,fontWeight:700,color:T.text,marginBottom:14 }}>⏱ Delay Transaction</div>
      <div style={{ display:"flex",gap:6,marginBottom:14 }}>
        {[{v:"days",l:"Days"},{v:"months",l:"Months"},{v:"date",l:"Exact Date"}].map(m=>(
          <button key={m.v} onClick={()=>setMode(m.v)} style={{ background:mode===m.v?T.blue+"22":"transparent",color:mode===m.v?T.blue:T.textSub,border:`1px solid ${mode===m.v?T.blue:T.border}`,borderRadius:RADIUS.sm,padding:"5px 12px",fontSize:12,fontFamily:FONT,cursor:"pointer",flex:1 }}>{m.l}</button>
        ))}
      </div>
      {mode!=="date"?(
        <div style={{ display:"flex",gap:8,alignItems:"center",marginBottom:14 }}>
          <span style={{ fontSize:13,color:T.textSub }}>Delay by</span>
          <input type="number" min={1} value={amount} onChange={e=>setAmount(e.target.value)}
            style={{ background:T.surface,border:`1px solid ${T.border}`,borderRadius:RADIUS.md,color:T.text,padding:"7px 10px",fontSize:15,width:70,outline:"none",fontFamily:FONT,fontWeight:700 }}/>
          <span style={{ fontSize:13,color:T.textSub }}>{mode}</span>
        </div>
      ):(
        <div style={{ marginBottom:14 }}><DateInp value={exactDate} onChange={setExactDate} placeholder="New date"/></div>
      )}
      <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
        <Btn onClick={onClose} variant="outline" size="sm" color={T.textSub}>Cancel</Btn>
        <Btn onClick={handleConfirm} size="sm" color={T.blue}>Apply Delay</Btn>
      </div>
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────
export function Card({ children, style={}, onClick }) {
  const T = useTheme();
  injectStyles();
  return (
    <div onClick={onClick} style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: RADIUS.lg, padding: 20,
      boxShadow: T.shadow,
      transition: "box-shadow 0.2s, transform 0.15s, border-color 0.2s",
      ...(onClick ? { cursor: "pointer" } : {}), ...style,
    }}
    onMouseEnter={onClick ? e => {
      e.currentTarget.style.transform = "translateY(-2px)";
      e.currentTarget.style.boxShadow = "0 12px 32px rgba(0,0,0,0.35)";
      e.currentTarget.style.borderColor = "rgba(139,92,246,0.25)";
    } : undefined}
    onMouseLeave={onClick ? e => {
      e.currentTarget.style.transform = "";
      e.currentTarget.style.boxShadow = "";
      e.currentTarget.style.borderColor = "";
    } : undefined}
    >{children}</div>
  );
}

// ─── Tab Content Wrapper (animated on mount) ──────────────────────────────────
export function TabContent({ children }) {
  injectStyles();
  return <div className="sf-tab-enter">{children}</div>;
}

// ─── Animated Item ────────────────────────────────────────────────────────────
export function AnimItem({ children, style={} }) {
  injectStyles();
  return <div className="sf-anim-slide-in" style={style}>{children}</div>;
}

// ─── Label ────────────────────────────────────────────────────────────────────
export function Label({ children, color, size=12, style={}, tip }) {
  const T = useTheme();
  return (
    <div style={{ fontSize:size,fontWeight:600,letterSpacing:0.5,textTransform:"uppercase",color:color||T.textSub||"#8b8fa8",display:"flex",alignItems:"center",gap:4,...style }}>
      {children}{tip&&<Info tip={tip}/>}
    </div>
  );
}

// ─── Select ───────────────────────────────────────────────────────────────────
export function Select({ value, onChange, children, style={}, tip }) {
  const T = useTheme();
  const sel = (
    <select value={value} onChange={onChange} style={{
      background:T.surface||"#131629",border:`1.5px solid ${T.border||"rgba(255,255,255,0.08)"}`,
      borderRadius:RADIUS.md,color:T.text||"#f0f2ff",padding:"9px 14px",fontSize:15,
      outline:"none",fontFamily:FONT,cursor:"pointer",transition:"border-color 0.15s",...style,
    }}>{children}</select>
  );
  if (tip) return <Tooltip content={tip}>{sel}</Tooltip>;
  return sel;
}

// ─── Toggle ───────────────────────────────────────────────────────────────────
export function Toggle({ value, onChange, tip }) {
  const T = useTheme();
  injectStyles();
  const tog = (
    <button onClick={()=>onChange(!value)} style={{
      width:44,height:26,borderRadius:RADIUS.pill,
      background:value?T.green||"#34c759":T.surfaceHi||"#1a1e35",
      border:"none",cursor:"pointer",position:"relative",
      transition:"background 0.2s",flexShrink:0,padding:0,
    }}>
      <span style={{ position:"absolute",top:3,left:value?21:3,width:20,height:20,borderRadius:"50%",background:"#fff",
        transition:"left 0.2s cubic-bezier(0.34,1.56,0.64,1)",boxShadow:"0 1px 4px rgba(0,0,0,0.3)" }}/>
    </button>
  );
  if (tip) return <Tooltip content={tip}>{tog}</Tooltip>;
  return tog;
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, width=520 }) {
  const T = useTheme();
  injectStyles();
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (open) {
      const handler = e => { if (e.key === "Escape") onClose(); };
      document.addEventListener("keydown", handler);
      return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", handler); };
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, onClose]);

  if (!open) return null;
  // Portal to document.body — without this the modal renders inside the
  // <main> element's zIndex:1 stacking context, which traps it behind
  // navbars, dropdowns, and any other fixed siblings of <main>. The
  // dialog itself stays position:fixed inset:0 so it covers everything.
  const dialog = (
    <div role="dialog" aria-modal="true" aria-label={title||"Dialog"} style={{ position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(16px)",WebkitBackdropFilter:"blur(16px)",display:"flex",alignItems:"center",justifyContent:"center",padding:"20px",overflowY:"auto",animation:"sfFadeIn 0.15s ease both" }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="sf-anim-modal" style={{ background:T.card||"#131629",border:`1px solid ${T.border||"rgba(255,255,255,0.08)"}`,borderRadius:RADIUS.xl,padding:32,width:"100%",maxWidth:width,boxShadow:"0 32px 80px rgba(0,0,0,0.6)",flexShrink:0,maxHeight:"90vh",overflowY:"auto",display:"flex",flexDirection:"column" }}>
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:24,flexShrink:0 }}>
          <div style={{ fontSize:19,fontWeight:700,color:T.text||"#f0f2ff",letterSpacing:-0.3 }}>{title}</div>
          <button onClick={onClose} aria-label="Close dialog" style={{ background:T.surfaceHi||"#1a1e35",border:"none",color:T.textSub||"#8b8fa8",width:28,height:28,borderRadius:"50%",cursor:"pointer",fontSize:14,fontFamily:FONT,display:"flex",alignItems:"center",justifyContent:"center",transition:"background 0.15s",flexShrink:0 }}
            onMouseEnter={e=>e.currentTarget.style.background="#ff3b3044"}
            onMouseLeave={e=>e.currentTarget.style.background=T.surfaceHi||"#1a1e35"}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
  return typeof document !== "undefined" ? ReactDOM.createPortal(dialog, document.body) : dialog;
}

// ─── EmptyState ───────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  const T = useTheme();
  injectStyles();
  return (
    <div style={{ textAlign:"center",padding:"48px 24px",animation:"sfFadeIn 0.3s ease both" }}>
      <div style={{ fontSize:40,marginBottom:16,display:"inline-block",animation:"sfWiggle 1s ease 0.3s both" }}>{icon}</div>
      <div style={{ fontSize:19,fontWeight:700,color:T.text,marginBottom:8 }}>{title}</div>
      <div style={{ fontSize:15,color:T.textSub,marginBottom:24,maxWidth:320,margin:"0 auto 24px" }}>{description}</div>
      {action}
    </div>
  );
}

// ─── SegmentedControl ─────────────────────────────────────────────────────────
export function SegmentedControl({ options, value, onChange }) {
  const T = useTheme();
  return (
    <div style={{ display:"inline-flex",background:T.surfaceHi||"#1a1e35",borderRadius:RADIUS.md,padding:2,gap:2 }}>
      {options.map(opt=>(
        <button key={opt.value} onClick={()=>onChange(opt.value)} style={{
          background:value===opt.value?T.card||"#131629":"transparent",
          color:value===opt.value?T.text||"#f0f2ff":T.textSub||"#8b8fa8",
          border:"none",borderRadius:RADIUS.sm-2,padding:"6px 14px",fontSize:13,
          fontWeight:value===opt.value?600:400,fontFamily:FONT,cursor:"pointer",
          transition:"all 0.15s cubic-bezier(0.34,1.56,0.64,1)",
          boxShadow:value===opt.value?"0 1px 4px rgba(0,0,0,0.2)":"none",
          transform:value===opt.value?"scale(1.02)":"scale(1)",
        }}>{opt.label}</button>
      ))}
    </div>
  );
}

// ─── Badge ────────────────────────────────────────────────────────────────────
export function Badge({ children, color, style={} }) {
  const T = useTheme();
  const c = color||T.blue||"#8b5cf6";
  return <span style={{ background:`${c+"20"}`,color:c,fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:RADIUS.pill,letterSpacing:0.3,...style }}>{children}</span>;
}

// ─── NumInp (zero hooks) ──────────────────────────────────────────────────────
export function NumInp({ value, onChange, placeholder, style={}, min, max, step, disabled, tip }) {
  const T = useTheme();
  const inp = (
    <input type="number" value={value === 0 ? "" : (value || "")} placeholder={placeholder||"0"}
      disabled={disabled} min={min} max={max} step={step}
      onChange={e => { const p = parseFloat(e.target.value); onChange(isNaN(p) ? 0 : p); }}
      onFocus={e => e.target.style.borderColor = T.blue || "#8b5cf6"}
      onBlur={e => { e.target.style.borderColor = T.border; }}
      style={{ background:T.surface||"#131629",border:`1.5px solid ${T.border||"rgba(255,255,255,0.08)"}`,borderRadius:RADIUS.md,color:T.text||"#f0f2ff",padding:"9px 14px",fontSize:15,outline:"none",fontFamily:FONT,transition:"border-color 0.15s",...style }}
    />
  );
  return <Tooltip content={tip}>{inp}</Tooltip>;
}

// ─── Divider ──────────────────────────────────────────────────────────────────
export function Divider({ style={} }) {
  const T = useTheme();
  return <div style={{ height:1,background:T.border||"rgba(255,255,255,0.08)",...style }}/>;
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
export function StatCard({ label, value, color, sub, tip, onClick }) {
  const T = useTheme();
  return (
    <Card onClick={onClick} style={{ padding:"16px 20px" }}>
      <Label tip={tip} style={{ marginBottom:6 }}>{label}</Label>
      <div style={{ fontSize:26,fontWeight:700,color:color||T.blue,letterSpacing:-0.5,marginTop:4 }}>{value}</div>
      {sub&&<div style={{ fontSize:12,color:T.textSub,marginTop:3 }}>{sub}</div>}
    </Card>
  );
}

// ─── SectionHeader ────────────────────────────────────────────────────────────
export function SectionHeader({ title, action, tip, description }) {
  const T = useTheme();
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center" }}>
        <div style={{ fontSize:19,fontWeight:700,color:T.text,letterSpacing:-0.3,display:"flex",alignItems:"center",gap:6 }}>
          {title}{tip&&<Info tip={tip}/>}
        </div>
        {action}
      </div>
      {description&&<div style={{ fontSize:14,color:T.textSub,marginTop:4 }}>{description}</div>}
    </div>
  );
}


// ─── SlidePanel — appears near trigger button, not full-screen blocking ────────
// Usage:
//   const btnRef = useRef(null);
//   <button ref={btnRef}>Open</button>
//   <SlidePanel open={open} onClose={onClose} title="Title" triggerRef={btnRef} width={480}>
//     ...form content...
//   </SlidePanel>
//
// Positioning: appears to the right of trigger if space, otherwise left or below.
// On mobile: always slides up from bottom as a sheet.

export function SlidePanel({ open, onClose, title, children, width=480, triggerRef }) {
  const T = useTheme();
  const panelRef = useRef(null);
  const [pos, setPos] = useState({ top:0, left:0, placement:"right" });

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "";

    function position() {
      if (!triggerRef?.current || !panelRef?.current) return;
      const tr = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const pw = Math.min(width, vw - 32);
      const ph = panelRef.current.offsetHeight || 600;

      // Try right of trigger
      if (tr.right + pw + 12 <= vw) {
        setPos({ top: Math.min(tr.top, vh - ph - 16), left: tr.right + 12, placement:"right" });
      }
      // Try left of trigger
      else if (tr.left - pw - 12 >= 0) {
        setPos({ top: Math.min(tr.top, vh - ph - 16), left: tr.left - pw - 12, placement:"left" });
      }
      // Fall below trigger (centered under it)
      else {
        const left = Math.max(16, Math.min(tr.left + tr.width/2 - pw/2, vw - pw - 16));
        setPos({ top: tr.bottom + 8, left, placement:"below" });
      }
    }

    requestAnimationFrame(position);
    window.addEventListener("resize", position);
    return () => window.removeEventListener("resize", position);
  }, [open, triggerRef]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    function onClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target) &&
          triggerRef?.current && !triggerRef.current.contains(e.target)) {
        onClose();
      }
    }
    setTimeout(() => document.addEventListener("mousedown", onClick), 100);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open, onClose]);

  if (!open) return null;

  const isMobile = window.innerWidth < 768;

  if (isMobile) {
    // Mobile: bottom sheet
    return (
      <>
        <div onClick={onClose} style={{ position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.55)",backdropFilter:"blur(16px)" }}/>
        <div ref={panelRef} style={{
          position:"fixed", bottom:0, left:0, right:0, zIndex:10000,
          background:T.card, borderRadius:`${RADIUS.xl}px ${RADIUS.xl}px 0 0`,
          border:`1px solid ${T.border}`, borderBottom:"none",
          boxShadow:"0 -20px 60px rgba(0,0,0,0.4)",
          maxHeight:"90vh", overflowY:"auto",
          animation:"sfSheetUp 0.28s cubic-bezier(0.34,1.2,0.64,1) both",
        }}>
          <div style={{ width:40,height:4,borderRadius:2,background:T.border,margin:"12px auto 0" }}/>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"16px 24px 12px" }}>
            <div style={{ fontSize:17,fontWeight:700,color:T.text,letterSpacing:-0.3 }}>{title}</div>
            <button onClick={onClose} style={{ background:T.surfaceHi,border:"none",color:T.textSub,width:28,height:28,borderRadius:"50%",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center" }}>✕</button>
          </div>
          <div style={{ padding:"0 24px 32px" }}>{children}</div>
        </div>
      </>
    );
  }

  // Desktop: floating panel near trigger
  return (
    <>
      {/* Subtle backdrop — doesn't block everything, just dims slightly */}
      <div onClick={onClose} style={{ position:"fixed",inset:0,zIndex:10000,background:"rgba(0,0,0,0.15)" }}/>
      <div ref={panelRef} style={{
        position:"fixed",
        top: Math.max(16, pos.top),
        left: pos.left,
        zIndex:10000,
        width: Math.min(width, window.innerWidth - 32),
        background: T.card,
        border: `1px solid ${T.borderHi}`,
        borderRadius: RADIUS.xl,
        boxShadow: "0 24px 80px rgba(0,0,0,0.45), 0 0 0 1px rgba(139,92,246,0.1)",
        maxHeight: "85vh",
        overflowY: "auto",
        animation: `sfPanelIn${pos.placement==="right"?"Right":pos.placement==="left"?"Left":"Down"} 0.25s cubic-bezier(0.34,1.2,0.64,1) both`,
      }}>
        {/* Header */}
        <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",padding:"20px 24px 16px",borderBottom:`1px solid ${T.border}`,position:"sticky",top:0,background:T.card,zIndex:1,borderRadius:`${RADIUS.xl}px ${RADIUS.xl}px 0 0` }}>
          <div style={{ fontSize:16,fontWeight:700,color:T.text,letterSpacing:-0.3 }}>{title}</div>
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <span style={{ fontSize:10,color:T.textMuted,fontFamily:FONT }}>Esc to close</span>
            <button onClick={onClose} style={{ background:T.surfaceHi,border:"none",color:T.textSub,width:28,height:28,borderRadius:"50%",cursor:"pointer",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center",transition:"background 0.15s" }}
              onMouseEnter={e=>e.currentTarget.style.background="#ef444444"}
              onMouseLeave={e=>e.currentTarget.style.background=T.surfaceHi}>✕</button>
          </div>
        </div>
        {/* Content */}
        <div style={{ padding:"20px 24px 24px" }}>{children}</div>
      </div>
    </>
  );
}

// ─── ConfirmModal — always renders at viewport center via portal ──────────────
// Use this for all delete confirmations across the app.
// Props: open, title, message, onConfirm, onCancel, confirmLabel, confirmColor
// ─── useClickOutside hook ─────────────────────────────────────────────────────
export function useClickOutside(ref, handler) {
  useEffect(() => {
    function listener(e) {
      if (!ref.current || ref.current.contains(e.target)) return;
      handler(e);
    }
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
}

// ─── ConfirmSheet — beautiful centered card, no heavy backdrop ────────────────
// Usage: <ConfirmSheet open={bool} title="..." message="..." onConfirm={fn} onCancel={fn} confirmLabel="Delete" danger/>
export function ConfirmSheet({ open, title, message, onConfirm, onCancel, confirmLabel="Delete", danger=true, icon, confirmColor }) {
  const T = useTheme();
  injectStyles();
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  // `confirmColor` overrides the danger/blue defaults — used for the
  // sync-ON path where the confirm button should read as a positive
  // action (green) instead of red/blue.
  const confirmBg = confirmColor || (danger ? (T.red||"#ff3b30") : (T.blue||"#8b5cf6"));

  return ReactDOM.createPortal(
    <>
      {/* Backdrop — subtle blur, above any BottomSheet (which uses 9999/10000) */}
      <div
        onClick={onCancel}
        style={{
          position:"fixed", inset:0, zIndex:10010,
          background:"rgba(0,0,0,0.32)",
          backdropFilter:"blur(8px)",
          WebkitBackdropFilter:"blur(16px)",
          animation:"sfFadeIn 0.15s ease both",
        }}
      />
      {/* Card — centered, compact, clean */}
      <div role="alertdialog" aria-modal="true" aria-label={title||"Confirmation"} style={{
        position:"fixed", left:"50%", top:"50%",
        transform:"translate(-50%,-50%)",
        zIndex:10011,
        background:T.card||"#2a2a2c",
        border:`1px solid ${T.border||"rgba(255,255,255,0.1)"}`,
        borderRadius:20,
        padding:"28px 28px 24px",
        width:"min(400px, calc(100vw - 40px))",
        boxShadow:"0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
        animation:"sfSheetIn 0.22s cubic-bezier(0.34,1.3,0.64,1) both",
        fontFamily:FONT,
      }}>
        <style>{`@keyframes sfSheetIn{from{opacity:0;transform:translate(-50%,-50%) scale(0.92)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>
        {/* Icon */}
        {icon && <div style={{ fontSize:32, marginBottom:12, textAlign:"center" }}>{icon}</div>}
        {/* Title */}
        <div style={{ fontSize:17, fontWeight:700, color:T.text||"#f0f2ff", marginBottom:8, lineHeight:1.3 }}>{title}</div>
        {/* Message */}
        {message && <div style={{ fontSize:13, color:T.textSub||"#8b8fa8", marginBottom:24, lineHeight:1.6 }}>{message}</div>}
        {/* Actions */}
        <div style={{ display:"flex", gap:10 }}>
          <button
            onClick={onCancel}
            style={{
              flex:1, background:"transparent",
              border:`1px solid ${T.border||"rgba(255,255,255,0.12)"}`,
              color:T.textSub||"#8b8fa8",
              borderRadius:12, padding:"11px 0",
              cursor:"pointer", fontSize:14, fontWeight:600,
              fontFamily:FONT, transition:"all 0.15s",
            }}
            onMouseEnter={e=>e.currentTarget.style.background=T.surface||"rgba(255,255,255,0.06)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex:1,
              background: confirmBg,
              border:"none",
              color:"#fff",
              borderRadius:12, padding:"11px 0",
              cursor:"pointer", fontSize:14, fontWeight:700,
              fontFamily:FONT, transition:"opacity 0.15s",
              boxShadow:`0 4px 16px ${confirmBg+"55"}`,
            }}
            onMouseEnter={e=>e.currentTarget.style.opacity="0.88"}
            onMouseLeave={e=>e.currentTarget.style.opacity="1"}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

// Keep old ConfirmModal as alias for backward compat
export function ConfirmModal({ open, title, message, onConfirm, onCancel, confirmLabel="Yes, Delete", confirmColor }) {
  return <ConfirmSheet open={open} title={title} message={message} onConfirm={onConfirm} onCancel={onCancel} confirmLabel={confirmLabel} danger={true}/>;
}

// ─── PromptSheet — centered card with text input, matches ConfirmSheet design ─
// Usage: <PromptSheet open={bool} title="..." placeholder="..." defaultValue="..."
//          onConfirm={(value)=>...} onCancel={fn} confirmLabel="Create"/>
export function PromptSheet({ open, title, message, placeholder="", defaultValue="", onConfirm, onCancel, confirmLabel="Confirm", icon }) {
  const T = useTheme();
  const [value, setValue] = useState(defaultValue || "");
  const inputRef = useRef(null);
  injectStyles();
  useEffect(() => {
    if (open) {
      setValue(defaultValue || "");
      document.body.style.overflow = "hidden";
      setTimeout(() => inputRef.current?.focus(), 80);
    } else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open, defaultValue]);

  if (!open) return null;

  function submit() { if (value.trim()) { onConfirm?.(value.trim()); } }

  return ReactDOM.createPortal(
    <>
      <div onClick={onCancel} style={{
        position:"fixed", inset:0, zIndex:10010,
        background:"rgba(0,0,0,0.32)",
        backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(16px)",
        animation:"sfFadeIn 0.15s ease both",
      }}/>
      <div role="dialog" aria-modal="true" aria-label={title||"Prompt"} style={{
        position:"fixed", left:"50%", top:"50%",
        transform:"translate(-50%,-50%)", zIndex:10011,
        background:T.card||"#2a2a2c",
        border:`1px solid ${T.border||"rgba(255,255,255,0.1)"}`,
        borderRadius:20, padding:"28px 28px 24px",
        width:"min(400px, calc(100vw - 40px))",
        boxShadow:"0 40px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
        animation:"sfSheetIn 0.22s cubic-bezier(0.34,1.3,0.64,1) both",
        fontFamily:FONT,
      }}>
        <style>{`@keyframes sfSheetIn{from{opacity:0;transform:translate(-50%,-50%) scale(0.92)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>
        {icon && <div style={{ fontSize:32, marginBottom:12, textAlign:"center" }}>{icon}</div>}
        <div style={{ fontSize:17, fontWeight:700, color:T.text||"#f0f2ff", marginBottom:8, lineHeight:1.3 }}>{title}</div>
        {message && <div style={{ fontSize:13, color:T.textSub||"#8b8fa8", marginBottom:12, lineHeight:1.6 }}>{message}</div>}
        <input ref={inputRef} type="text" value={value} placeholder={placeholder}
          onChange={e=>setValue(e.target.value)}
          onKeyDown={e=>{ if (e.key==="Enter") submit(); if (e.key==="Escape") onCancel?.(); }}
          style={{
            width:"100%", boxSizing:"border-box", padding:"11px 14px",
            background:T.surface||"rgba(255,255,255,0.06)",
            border:`1.5px solid ${T.accent||"#8b5cf6"}44`,
            borderRadius:12, color:T.text||"#f0f2ff",
            fontSize:14, fontFamily:FONT, outline:"none",
            marginBottom:20,
            transition:"border-color 0.15s",
          }}
          onFocus={e=>e.currentTarget.style.borderColor=(T.accent||"#8b5cf6")}
          onBlur={e=>e.currentTarget.style.borderColor=(T.accent||"#8b5cf6")+"44"}
        />
        <div style={{ display:"flex", gap:10 }}>
          <button onClick={onCancel} style={{
            flex:1, background:"transparent",
            border:`1px solid ${T.border||"rgba(255,255,255,0.12)"}`,
            color:T.textSub||"#8b8fa8", borderRadius:12, padding:"11px 0",
            cursor:"pointer", fontSize:14, fontWeight:600, fontFamily:FONT, transition:"all 0.15s",
          }}
            onMouseEnter={e=>e.currentTarget.style.background=T.surface||"rgba(255,255,255,0.06)"}
            onMouseLeave={e=>e.currentTarget.style.background="transparent"}
          >Cancel</button>
          <button onClick={submit} disabled={!value.trim()} style={{
            flex:1, background:T.accent||"#8b5cf6",
            border:"none", color:"#fff", borderRadius:12, padding:"11px 0",
            cursor: value.trim() ? "pointer" : "not-allowed",
            fontSize:14, fontWeight:700, fontFamily:FONT,
            opacity: value.trim() ? 1 : 0.5,
            transition:"opacity 0.15s",
            boxShadow:`0 4px 16px ${(T.accent||"#8b5cf6")}55`,
          }}
            onMouseEnter={e=>{ if(value.trim()) e.currentTarget.style.opacity="0.88"; }}
            onMouseLeave={e=>{ e.currentTarget.style.opacity = value.trim() ? "1" : "0.5"; }}
          >{confirmLabel}</button>
        </div>
      </div>
    </>,
    document.body
  );
}

// ─── SaveBadge — shows save status next to item names ─────────────────────────
// Usage: <SaveBadge status={saveStatus} show={lastEditedId === item.id}/>
export function SaveBadge({ status, show }) {
  if (!show) return null;
  const T = useTheme();
  if (status === "saving") return (
    <span aria-live="polite" style={{ display:"inline-flex",alignItems:"center",gap:4,fontSize:13,color:T.orange||"#ff9500",fontWeight:600,flexShrink:0,animation:"sfFadeIn 0.15s ease both" }}>
      <span style={{ animation:"sfSpin 0.8s linear infinite",display:"inline-block" }}>⟳</span> Saving…
    </span>
  );
  if (status === "saved") return (
    <span style={{ display:"inline-flex",alignItems:"center",gap:4,fontSize:13,color:T.green||"#34c759",fontWeight:700,flexShrink:0,animation:"sfFadeIn 0.2s ease both" }}>
      ✓ Saved
    </span>
  );
  if (status === "error") return (
    <span style={{ fontSize:12,color:T.red||"#ff3b30",fontWeight:600,flexShrink:0 }}>✕ Failed</span>
  );
  return null;
}

// ─── ValidationMsg ────────────────────────────────────────────────────────────
export function ValidationMsg({ message, style={} }) {
  const T = useTheme();
  if (!message) return null;
  return <div style={{ fontSize:11,color:T.red||"#ff3b30",marginTop:4,display:"flex",alignItems:"center",gap:4,animation:"sfSlideIn 0.2s ease both",...style }}><span>⚠</span>{message}</div>;
}

// ─── ThemeModeSwitcher ────────────────────────────────────────────────────────
export function ThemeModeSwitcher({ mode, onChange }) {
  return <SegmentedControl options={[{value:"light",label:"☀️"},{value:"auto",label:"⚙️"},{value:"dark",label:"🌙"}]} value={mode} onChange={onChange}/>;
}

// ─── Amount ───────────────────────────────────────────────────────────────────
export function Amount({ value, type="neutral", size=15 }) {
  const T = useTheme();
  const color = type==="income"?T.income:type==="expense"?T.expense:T.text;
  const prefix = type==="income"?"+":type==="expense"?"-":"";
  const abs = Math.abs(value||0);
  let display;
  if(abs>=1e6) display=(abs/1e6).toFixed(1)+"M";
  else if(abs>=1000) display=(abs/1000).toFixed(1)+"K";
  else display=abs.toLocaleString("en-US",{minimumFractionDigits:0,maximumFractionDigits:0});
  return <span style={{ fontSize:size,fontWeight:600,color,fontVariantNumeric:"tabular-nums" }}>{prefix}€{display}</span>;
}

// ─── BottomSheet ──────────────────────────────────────────────────────────────
// A mobile-first bottom sheet that slides up from the bottom edge.
// On desktop it still appears as a centered bottom-anchored sheet (max 640px wide).
// Drag the handle downward past 20% of sheet height to dismiss.
//
// Usage:
//   <BottomSheet open={open} onClose={onClose} title="Add Transaction" height="85vh">
//     {children}
//   </BottomSheet>
//
// Props:
//   open       boolean
//   onClose    fn
//   title      string|node — shown in sticky header
//   subtitle   string|node — small text below title
//   height     string css value, default "85vh"
//   maxWidth   number px, default 640
//   footer     node — pinned to bottom (buttons row)
//   noPad      boolean — remove body padding (for full-bleed content)

export function BottomSheet({ open, onClose, title, subtitle, height="85vh", maxWidth=980, footer, noPad=false, children }) {
  const T = useTheme();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [dragY, setDragY] = useState(0);          // pixels dragged down (positive = down)
  const [isDragging, setIsDragging] = useState(false);
  const sheetRef = useRef(null);
  const dragStartY = useRef(null);
  const dragStartScrollTop = useRef(0);
  const bodyRef = useRef(null);

  // Mount → animate in
  useEffect(() => {
    if (open) {
      setMounted(true);
      document.body.classList.add("sf-overlay-open");
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      document.body.classList.remove("sf-overlay-open");
      const t = setTimeout(() => setMounted(false), 320);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Dismiss on Escape
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // ── Drag-to-dismiss ──────────────────────────────────────────────────────────
  function onHandlePointerDown(e) {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartScrollTop.current = bodyRef.current?.scrollTop ?? 0;
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
  }

  const onPointerMove = useCallback((e) => {
    const delta = e.clientY - dragStartY.current;
    setDragY(Math.max(0, delta)); // only allow dragging down
  }, []);

  const onPointerUp = useCallback(() => {
    setIsDragging(false);
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    const sheetH = sheetRef.current?.offsetHeight ?? 300;
    // Dismiss if dragged more than 20% of sheet height or 80px
    if (dragY > Math.min(sheetH * 0.2, 80)) {
      setDragY(0);
      onClose();
    } else {
      setDragY(0);
    }
  }, [dragY, onClose, onPointerMove]);

  if (!mounted) return null;

  const translateY = visible ? `${dragY}px` : "100%";
  const transition = isDragging ? "none" : `transform 0.32s cubic-bezier(0.32,0.72,0,1)`;

  return ReactDOM.createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 9999,
          background: "rgba(0,0,0,0.5)",
          backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
          opacity: visible ? 1 : 0,
          transition: "opacity 0.28s ease",
          touchAction: "none",
        }}
      />

      {/* Sheet — minimum 70vw / 70vh, capped at 98vw and 95vh */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title||"Panel"}
        style={{
          position: "fixed",
          bottom: 0, left: "50%",
          transform: `translateX(-50%) translateY(${translateY})`,
          transition,
          // Width: at least 70vw, capped at the page-level maxWidth (default 980px), never wider than 98vw
          width: `min(98vw, max(70vw, ${typeof maxWidth === "number" ? maxWidth+"px" : maxWidth}))`,
          // Height: at least 70vh (user requirement), never taller than 95vh
          height: `min(95dvh, max(70vh, ${height}))`,
          maxHeight: "95dvh",
          zIndex: 10000,
          display: "flex", flexDirection: "column",
          background: T.card,
          borderRadius: "20px 20px 0 0",
          boxShadow: "0 -8px 40px rgba(0,0,0,0.4)",
          border: `1px solid ${T.border}`,
          borderBottom: "none",
          willChange: "transform",
          touchAction: "none",
          // Inherit app font and a sensible minimum size for everything inside
          fontFamily: FONT,
          fontSize: 13,
          color: T.text,
        }}
      >
        {/* Drag handle */}
        <div
          onPointerDown={onHandlePointerDown}
          style={{
            flexShrink: 0,
            padding: "10px 0 6px",
            cursor: "grab",
            display: "flex", flexDirection: "column", alignItems: "center", gap: 0,
            userSelect: "none",
            touchAction: "none",
          }}
        >
          <div style={{
            width: 36, height: 4, borderRadius: 2,
            background: T.border,
            transition: "background 0.15s",
          }}/>
        </div>

        {/* Header */}
        {(title || subtitle) && (
          <div style={{
            flexShrink: 0,
            padding: "4px 28px 16px",
            borderBottom: `1px solid ${T.border}`,
          }}>
            {title && (
              <div style={{ fontSize: 19, fontWeight: 800, color: T.text, letterSpacing: -0.3, fontFamily: FONT }}>
                {title}
              </div>
            )}
            {subtitle && (
              <div style={{ fontSize: 13, color: T.textSub, marginTop: 4, fontFamily: FONT, lineHeight: 1.5 }}>{subtitle}</div>
            )}
          </div>
        )}

        {/* Scrollable body — base font 13 (min 12 enforced via inline-style cascade) */}
        <div
          ref={bodyRef}
          style={{
            flex: 1,
            overflowY: "auto",
            overscrollBehavior: "contain",
            padding: noPad ? 0 : "20px 28px",
            WebkitOverflowScrolling: "touch",
            fontFamily: FONT,
            fontSize: 13,
            color: T.text,
            lineHeight: 1.55,
          }}
        >
          {children}
        </div>

        {/* Pinned footer — bottom padding honours iOS home-indicator safe
            area so buttons don't sit underneath it on iPhone X+ */}
        {footer && (
          <div style={{
            flexShrink: 0,
            padding: "14px 28px max(14px, env(safe-area-inset-bottom))",
            borderTop: `1px solid ${T.border}`,
            background: T.card,
            display: "flex", gap: 12,
            fontFamily: FONT,
          }}>
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body
  );
}
